import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWorkItemsByProjectId,
  createWorkItem,
} from "../../Redux/Thunks/projectManagementThunk";
import WorkItemTypeIcon from "./WorkItemTypeIcon";
import Button from "../../Utility/Button/Button";
import Typography from "../../Utility/Typography";
import Input from "../../Utility/Input/Input";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import { formatDateForInput } from "../../utils/dateUtils";
import { showSuccess, showError } from "../../utils/toast";
import { getApiErrorMessage, isSilentRequestAbortError } from "../../utils/apiErrorMessage";
import Loader from "../../Utility/Loader/Loader";
import { selectEpicChildWorkItemsLoading } from "../../Redux/Slices/issuesSlice";
import { selectAuthUserId } from "../../Redux/Slices/authSlice";
import { mapSystemListItemToOption, getAssigneeDisplay } from "./issueMappers";
import WorkItemCopyLinkButton from "./WorkItemCopyLinkButton";
import { copyWorkItemDetailLink } from "../../utils/workItemDetailUrl";
import EpicChildProgressBar from "./EpicChildProgressBar";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import { EMPTY_EPIC_PROGRESS, resolveEpicProgressDisplay } from "./epicChildIssuesUtils";

/**
 * Lists work items whose parent is this epic, plus Jira-style quick add (Task/Bug, title, assignee).
 */
export default function EpicChildIssuesPanel({
  projectSlug,
  epicWorkItemId,
  workItemTypes = [],
  priorityOptions = [],
  assigneeOptions = [],
  /** System list rows for SEVERITY — used to default new bugs to Major. */
  severityList = [],
  onOpenChildWorkItem,
  canCreateWorkItem = false,
}) {
  const dispatch = useDispatch();
  const loggedInUserId = useSelector(selectAuthUserId);
  const [rows, setRows] = useState([]);
  const [epicProgress, setEpicProgress] = useState(EMPTY_EPIC_PROGRESS);
  const loading = useSelector((state) =>
    selectEpicChildWorkItemsLoading(state, projectSlug, epicWorkItemId)
  );
  const [quickKind, setQuickKind] = useState("task");
  const [quickTitle, setQuickTitle] = useState("");
  const [quickAssigneeId, setQuickAssigneeId] = useState("");
  const [creating, setCreating] = useState(false);
  const [hideDone, setHideDone] = useState(true);

  const taskTypeId = useMemo(() => {
    const row = workItemTypes.find(
      (t) =>
        String(t?.name ?? t?.value ?? "")
          .trim()
          .toLowerCase() === "task"
    );
    return row != null ? String(row.id ?? row.value ?? "").trim() || null : null;
  }, [workItemTypes]);

  const bugTypeId = useMemo(() => {
    const row = workItemTypes.find(
      (t) =>
        String(t?.name ?? t?.value ?? "")
          .trim()
          .toLowerCase() === "bug"
    );
    return row != null ? String(row.id ?? row.value ?? "").trim() || null : null;
  }, [workItemTypes]);

  const mediumPriorityId = useMemo(() => {
    const opts = Array.isArray(priorityOptions) ? priorityOptions : [];
    const hit =
      opts.find(
        (o) =>
          String(o.label ?? "")
            .trim()
            .toLowerCase() === "medium"
      ) ||
      opts.find(
        (o) =>
          String(o.value ?? "")
            .trim()
            .toLowerCase() === "medium"
      );
    return hit != null ? String(hit.value ?? hit.id ?? "").trim() || null : null;
  }, [priorityOptions]);

  const majorSeverityId = useMemo(() => {
    const list = Array.isArray(severityList) ? severityList : [];
    for (const item of list) {
      const opt = mapSystemListItemToOption(item);
      if (
        String(opt.label ?? "")
          .trim()
          .toLowerCase() === "major"
      ) {
        const v = String(opt.value ?? opt.id ?? "").trim();
        return v || null;
      }
    }
    const row = list.find(
      (s) =>
        String(s?.name ?? s?.value ?? "")
          .trim()
          .toLowerCase() === "major"
    );
    if (row == null) return null;
    return String(row.id ?? row.value ?? "").trim() || null;
  }, [severityList]);

  const loadChildren = useCallback(
    async (hideDoneFilter) => {
      if (!projectSlug || !epicWorkItemId) {
        setRows([]);
        setEpicProgress(EMPTY_EPIC_PROGRESS);
        return;
      }
      const hideDoneParam = hideDoneFilter != null ? hideDoneFilter : true;
      try {
        const epic = String(epicWorkItemId);
        const data = await dispatch(
          fetchWorkItemsByProjectId({
            projectId: String(projectSlug),
            body: {
              limit: 100,
              page: 1,
              parentIds: [epic],
              hideDone: hideDoneParam,
            },
            skipIssuesSlice: true,
          })
        ).unwrap();
        const list = Array.isArray(data?.list) ? data.list : [];
        setRows(list);
        setEpicProgress(resolveEpicProgressDisplay(data, list));
      } catch (e) {
        if (isSilentRequestAbortError(e)) return;
        setRows([]);
        setEpicProgress(EMPTY_EPIC_PROGRESS);
        const msg = getApiErrorMessage(e, "Failed to load issues in this epic.");
        if (String(msg ?? "").trim()) showError(msg);
      }
    },
    [dispatch, projectSlug, epicWorkItemId]
  );

  useEffect(() => {
    if (!projectSlug || !epicWorkItemId) {
      setRows([]);
      setEpicProgress(EMPTY_EPIC_PROGRESS);
      return;
    }
    setHideDone(true);
  }, [projectSlug, epicWorkItemId]);

  useEffect(() => {
    if (!projectSlug || !epicWorkItemId) return;
    void loadChildren(hideDone);
  }, [projectSlug, epicWorkItemId, hideDone, loadChildren]);

  const selectedTypeId = quickKind === "bug" ? bugTypeId : taskTypeId;

  const resetQuick = useCallback(() => {
    setQuickTitle("");
    setQuickAssigneeId("");
    setQuickKind("task");
  }, []);

  const handleCopyChildLink = useCallback(
    async (wi) => {
      const workItemId = wi?.id ?? wi?.workItemId;
      if (!projectSlug || !workItemId) {
        showError("Cannot copy link: missing project or work item.");
        return;
      }
      try {
        await copyWorkItemDetailLink({
          projectSlug,
          workItemId: String(workItemId),
        });
        showSuccess("Link copied to clipboard.");
      } catch {
        showError("Could not copy link. Check browser permissions.");
      }
    },
    [projectSlug]
  );

  const handleQuickCreate = async () => {
    const title = String(quickTitle ?? "").trim();
    if (!title) {
      showError("Enter a title.");
      return;
    }
    if (!selectedTypeId) {
      showError("Task and Bug types are not available yet.");
      return;
    }
    if (quickKind === "bug" && !majorSeverityId) {
      showError("Could not resolve Major severity for new bugs.");
      return;
    }
    setCreating(true);
    try {
      const startDate = formatDateForInput(new Date()) || null;
      const body = {
        typeId: selectedTypeId,
        parentId: String(epicWorkItemId),
        title,
        description: null,
        ...(mediumPriorityId ? { priorityId: mediumPriorityId } : {}),
        assigneeId: quickAssigneeId ? String(quickAssigneeId) : loggedInUserId || null,
        originalEstimate: null,
        storyPoints: 0,
        startDate,
        dueDate: null,
      };
      if (quickKind === "bug") {
        body.bugDetails = {
          severityId: majorSeverityId,
          environment: null,
          reproductionSteps: null,
        };
      }
      await dispatch(createWorkItem({ projectId: String(projectSlug), body })).unwrap();
      showSuccess("Work item created.");
      resetQuick();
      await loadChildren(hideDone);
    } catch (e) {
      if (!isSilentRequestAbortError(e)) {
        showError(getApiErrorMessage(e, "Failed to create work item."));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleHideDoneChange = (e) => {
    setHideDone(e.target.checked);
  };

  return (
    <section
      className={`relative max-h-[450px] rounded-lg border-2 border-dashed border-emerald-300/80 bg-emerald-50/40 p-4 shadow-sm ${
        loading ? "overflow-hidden" : "overflow-y-auto"
      }`}
      aria-labelledby="epic-children-heading"
      aria-busy={loading}
    >
      {loading ? (
        <div className="relative min-h-[220px]">
          <Loader variant="content" label="Loading issues in this epic" />
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Typography
                id="epic-children-heading"
                variant="h5"
                className="!mb-0 inline-flex flex-wrap items-center gap-2 text-text-primary"
              >
                <span className="inline-flex shrink-0 items-center" aria-hidden>
                  <WorkItemTypeIcon typeLabel="Epic" sizeClassName="text-[1.125rem]" />
                </span>
                <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Epic
                </span>
                <span>Issues in this epic</span>
              </Typography>
              <Typography variant="text2" className="mt-1 text-text-secondary">
                Child tasks and bugs linked to this epic.
              </Typography>
            </div>
            <div className="epic-child-toolbar shrink-0">
              <Checkbox
                name="epic-hide-done"
                checked={hideDone}
                onChange={handleHideDoneChange}
                label="Hide done"
                className="epic-child-toolbar__filter"
              />
            </div>
          </div>

          <EpicChildProgressBar
            className="mb-4"
            doneCount={epicProgress.done}
            totalCount={epicProgress.total}
          />

          {canCreateWorkItem ? (
            <div className="mb-4 rounded-lg border border-dashed border-border-primary bg-white/90 p-3">
              <Typography
                variant="label"
                className="mb-2 block text-xs font-semibold text-text-secondary"
              >
                Quick add
              </Typography>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="inline-flex shrink-0 rounded-md border border-border-primary bg-surface-secondary p-0.5">
                  <button
                    type="button"
                    onClick={() => setQuickKind("task")}
                    className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm font-medium transition-colors ${
                      quickKind === "task"
                        ? "bg-white text-[color:var(--work-item-type-task)] shadow-sm"
                        : "text-text-secondary hover:text-text-primary"
                    }`}
                  >
                    <WorkItemTypeIcon
                      typeLabel="Task"
                      muted={quickKind !== "task"}
                      sizeClassName="text-base"
                    />
                    Task
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickKind("bug")}
                    className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm font-medium transition-colors ${
                      quickKind === "bug"
                        ? "bg-white text-[color:var(--work-item-type-bug)] shadow-sm"
                        : "text-text-secondary hover:text-text-primary"
                    }`}
                  >
                    <WorkItemTypeIcon
                      typeLabel="Bug"
                      muted={quickKind !== "bug"}
                      sizeClassName="text-base"
                    />
                    Bug
                  </button>
                </div>

                <Input
                  type="text"
                  noWrapper
                  value={quickTitle}
                  onChange={(e) => setQuickTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void handleQuickCreate();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      resetQuick();
                    }
                  }}
                  placeholder="What needs to be done?"
                  disabled={creating}
                  aria-label="Summary for new work item"
                  className=" min-w-[min(100%,12rem)] flex-1 px-3 py-1.5 text-sm text-text-primary"
                />

                <div className="w-full min-w-[12rem] sm:w-52">
                  <Dropdown
                    name="epic-quick-assignee"
                    value={quickAssigneeId}
                    onChange={(e) => setQuickAssigneeId(e.target.value)}
                    options={assigneeOptions}
                    placeholder="Assignee"
                    noWrapper={true}
                    disabled={creating}
                    searchable={true}
                    clearable={true}
                    clearOptionLabel="Unassigned"
                  />
                </div>

                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={creating || !String(quickTitle).trim()}
                  className="shrink-0 sm:ml-auto"
                  onClick={() => void handleQuickCreate()}
                >
                  {creating ? "Creating…" : "Create"}
                </Button>
              </div>
              <Typography variant="text2" className="mt-2 text-xs text-text-secondary">
                Tip: Press Enter to create · Press Esc to clear
              </Typography>
            </div>
          ) : (
            <Typography variant="text2" className="mb-4 text-text-secondary">
              You do not have permission to create work items in this epic.
            </Typography>
          )}

          <div className="min-h-[120px] overflow-x-auto rounded-md border border-border-primary bg-white">
            {rows.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-text-secondary">
                {epicProgress.total === 0
                  ? "No child work items yet. Use quick add above."
                  : hideDone
                    ? "All issues are done. Uncheck Hide done to view them."
                    : "No child work items to show."}
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border-primary bg-surface-secondary text-text-secondary">
                  <tr>
                    <th className="px-3 py-2 font-medium">Key</th>
                    <th className="px-3 py-2 font-medium">Summary</th>
                    <th className="px-3 py-2 font-medium">Assigned to</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((wi) => {
                    const assignedTo = getAssigneeDisplay(wi.assignee);
                    return (
                      <tr
                        key={wi.id ?? wi.issueKey}
                        className="cursor-pointer border-b border-border-primary last:border-0 hover:bg-surface-secondary/60"
                        onClick={() => {
                          const id = wi.id ?? wi.workItemId;
                          if (id && onOpenChildWorkItem) onOpenChildWorkItem(String(id));
                        }}
                      >
                        <td className="px-3 py-2 align-middle">
                          <span className="inline-flex items-center gap-1.5 font-medium text-text-link">
                            <WorkItemTypeIcon typeLabel={wi.type} sizeClassName="text-base" />
                            {wi.issueKey || "—"}
                            {projectSlug && (wi.id ?? wi.workItemId) ? (
                              <WorkItemCopyLinkButton
                                className="shrink-0"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  void handleCopyChildLink(wi);
                                }}
                              />
                            ) : null}
                          </span>
                        </td>
                        <td className="max-w-[min(28rem,50vw)] px-3 py-2 align-middle text-text-primary">
                          <span className="line-clamp-2">{wi.title || "—"}</span>
                        </td>
                        <td className="min-w-[8rem] max-w-[14rem] whitespace-nowrap px-3 py-2 align-middle text-text-primary">
                          {assignedTo ? (
                            <span className="truncate" title={assignedTo}>
                              {assignedTo}
                            </span>
                          ) : (
                            <span className="text-text-secondary">Unassigned</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 align-middle text-text-secondary">
                          {wi.status || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}
